import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Check, Star, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ['Elige tu bono', 'Tus datos', 'Pago seguro'];

export const BonosPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ categories: {}, bonos: [] });
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: '',
    preferred_date: '',
    preferred_time: '',
    num_adults: 1,
    num_children: 0,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/bonos`)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Error al cargar el catálogo'))
      .finally(() => setLoading(false));
  }, []);

  const onSelect = (bono) => {
    setSelected(bono);
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onSubmitForm = (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.customer_phone) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    if (form.num_children > 0 && form.num_adults < 1) {
      toast.error('Los menores de 14 años deben ir acompañados por un adulto');
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onPay = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bonos/purchase`, {
        bono_id: selected.id,
        ...form,
      });
      const { redsys_url, payment_data } = res.data;
      const formEl = document.createElement('form');
      formEl.method = 'POST';
      formEl.action = redsys_url;
      Object.entries(payment_data).forEach(([k, v]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = v;
        formEl.appendChild(input);
      });
      document.body.appendChild(formEl);
      formEl.submit();
    } catch (err) {
      setSubmitting(false);
      toast.error(err.response?.data?.detail || 'Error al iniciar el pago');
    }
  };

  return (
    <div className="min-h-screen bg-halo-cream" data-testid="bonos-page">
      {/* Hero */}
      <section className="py-16 bg-halo-charcoal">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading text-4xl md:text-5xl text-halo-cream mb-4">
              Bonos HALO
            </h1>
            <p className="text-halo-cream/70 text-base md:text-lg max-w-xl mx-auto">
              Tratamientos completos al mejor precio. Elige tu bono, paga online y
              reserva tus sesiones llamándonos.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Progress */}
      {step > 0 && (
        <section className="py-6 bg-halo-surface border-b border-halo-taupe/10">
          <div className="max-w-3xl mx-auto px-6">
            <div className="flex items-center justify-between">
              {STEPS.map((label, i) => (
                <div key={label} className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-9 h-9 rounded-full font-medium text-sm transition-all ${
                      i < step
                        ? 'bg-halo-sage text-halo-cream'
                        : i === step
                        ? 'bg-halo-brown text-halo-cream'
                        : 'bg-halo-cream text-halo-text border border-halo-taupe/20'
                    }`}
                  >
                    {i < step ? <Check size={16} /> : i + 1}
                  </div>
                  <span
                    className={`ml-2 text-sm hidden sm:block ${
                      i <= step ? 'text-halo-charcoal font-medium' : 'text-halo-text'
                    }`}
                  >
                    {label}
                  </span>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`w-8 sm:w-12 h-0.5 mx-2 sm:mx-4 ${
                        i < step ? 'bg-halo-sage' : 'bg-halo-taupe/20'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          {loading && (
            <div className="text-center py-20">
              <Loader2 className="animate-spin mx-auto text-halo-brown" size={40} />
            </div>
          )}

          {!loading && step === 0 && <CatalogStep data={data} onSelect={onSelect} />}

          {step === 1 && (
            <FormStep
              selected={selected}
              form={form}
              setForm={setForm}
              onSubmit={onSubmitForm}
              onBack={() => setStep(0)}
            />
          )}

          {step === 2 && (
            <PaymentStep
              selected={selected}
              form={form}
              submitting={submitting}
              onPay={onPay}
              onBack={() => setStep(1)}
            />
          )}
        </div>
      </section>
    </div>
  );
};


const CatalogStep = ({ data, onSelect }) => {
  const orderedCats = ['individual', 'adulto_nino', 'yoga'];
  return (
    <div className="space-y-16">
      {orderedCats.map((catId) => {
        const cat = data.categories[catId];
        if (!cat) return null;
        return (
          <div key={catId}>
            <div className="text-center mb-8">
              <p className="text-halo-brown font-medium text-sm tracking-widest uppercase mb-2">
                {cat.icon} {cat.label}
              </p>
              <h2 className="font-heading text-3xl text-halo-charcoal mb-2">
                {cat.label}
              </h2>
              <p className="text-halo-text max-w-xl mx-auto">{cat.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {cat.items.map((bono) => (
                <BonoCard key={bono.id} bono={bono} onSelect={onSelect} />
              ))}
            </div>
          </div>
        );
      })}

      <div className="bg-halo-surface rounded-2xl p-8 border border-halo-taupe/10 text-center">
        <h3 className="font-heading text-2xl text-halo-charcoal mb-2">
          ¿Cómo reservo cada sesión?
        </h3>
        <p className="text-halo-text max-w-xl mx-auto mb-6">
          Tras tu compra recibirás un código y un PDF por email. Llámanos al{' '}
          <a href="tel:678901086" className="text-halo-brown font-medium">
            678 901 086
          </a>{' '}
          o escríbenos a{' '}
          <a href="mailto:info@halocuevas.com" className="text-halo-brown font-medium">
            info@halocuevas.com
          </a>{' '}
          indicando tu código y la fecha/hora preferida.
        </p>
      </div>
    </div>
  );
};


const BonoCard = ({ bono, onSelect }) => {
  return (
    <motion.div
      whileHover={{ y: -3 }}
      className={`relative bg-halo-surface rounded-2xl p-6 border transition-all ${
        bono.highlight
          ? 'border-halo-brown shadow-lg ring-2 ring-halo-brown/20'
          : 'border-halo-taupe/10'
      }`}
      data-testid={`bono-card-${bono.id}`}
    >
      {bono.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-halo-brown text-halo-cream px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
          <Star size={12} fill="currentColor" />
          Recomendado
        </div>
      )}

      <h3 className="font-heading text-lg text-halo-charcoal mb-1">{bono.name}</h3>
      <p className="text-xs text-halo-text mb-4">
        {bono.num_sessions} {bono.num_sessions === 1 ? 'sesión' : 'sesiones'}
      </p>

      <div className="mb-6">
        <p className="font-heading text-3xl text-halo-brown">{bono.price}€</p>
        {bono.is_pack && (
          <p className="text-sm text-halo-text mt-1">
            {bono.price_per_session?.toFixed(2)}€/sesión
          </p>
        )}
      </div>

      <Button
        onClick={() => onSelect(bono)}
        className="w-full bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
        data-testid={`buy-${bono.id}`}
      >
        {bono.is_pack ? 'Comprar bono' : 'Comprar'}
      </Button>
    </motion.div>
  );
};


const FormStep = ({ selected, form, setForm, onSubmit, onBack }) => (
  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
    <div className="flex items-center gap-4 mb-8">
      <button
        onClick={onBack}
        className="p-2 rounded-lg hover:bg-halo-brown/10 transition-colors"
        data-testid="back-to-catalog"
      >
        <ArrowLeft size={20} className="text-halo-charcoal" />
      </button>
      <div>
        <h2 className="font-heading text-2xl text-halo-charcoal">Tus datos</h2>
        <p className="text-halo-text text-sm">{selected.name}</p>
      </div>
    </div>

    <form onSubmit={onSubmit} className="bg-halo-surface rounded-2xl p-8 border border-halo-taupe/10 max-w-2xl">
      <div className="space-y-5">
        <div>
          <Label htmlFor="customer_name">Nombre completo *</Label>
          <Input
            id="customer_name"
            value={form.customer_name}
            onChange={(e) => setForm((p) => ({ ...p, customer_name: e.target.value }))}
            placeholder="Tu nombre"
            className="mt-2"
            data-testid="bono-input-name"
            required
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="customer_email">Email *</Label>
            <Input
              id="customer_email"
              type="email"
              value={form.customer_email}
              onChange={(e) =>
                setForm((p) => ({ ...p, customer_email: e.target.value }))
              }
              placeholder="tu@email.com"
              className="mt-2"
              data-testid="bono-input-email"
              required
            />
          </div>
          <div>
            <Label htmlFor="customer_phone">Teléfono *</Label>
            <Input
              id="customer_phone"
              type="tel"
              value={form.customer_phone}
              onChange={(e) =>
                setForm((p) => ({ ...p, customer_phone: e.target.value }))
              }
              placeholder="678 901 086"
              className="mt-2"
              data-testid="bono-input-phone"
              required
            />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="num_adults">Adultos *</Label>
            <Input
              id="num_adults"
              type="number"
              min="1"
              value={form.num_adults}
              onChange={(e) => setForm((p) => ({ ...p, num_adults: parseInt(e.target.value) || 1 }))}
              className="mt-2"
              data-testid="bono-input-adults"
              required
            />
          </div>
          <div>
            <Label htmlFor="num_children">
              Niños <span className="text-xs text-halo-text">(hasta 14 años)</span>
            </Label>
            <Input
              id="num_children"
              type="number"
              min="0"
              value={form.num_children}
              onChange={(e) => setForm((p) => ({ ...p, num_children: parseInt(e.target.value) || 0 }))}
              className="mt-2"
              data-testid="bono-input-children"
            />
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900">
          ⚠️ <strong>Importante:</strong> Los menores de 14 años deben ir acompañados <strong>siempre</strong> por
          un adulto. Tanto adultos como niños deben adquirir su bono.
        </div>

        <div>
          <Label className="text-halo-charcoal">Primera sesión preferida (opcional)</Label>
          <p className="text-xs text-halo-text mb-2">
            Si lo dejas en blanco, te llamaremos para concertar fecha y hora.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input
              type="date"
              value={form.preferred_date}
              onChange={(e) => setForm((p) => ({ ...p, preferred_date: e.target.value }))}
              className="mt-1"
              data-testid="bono-input-preferred-date"
              min={new Date().toISOString().split('T')[0]}
            />
            <Input
              type="time"
              value={form.preferred_time}
              onChange={(e) => setForm((p) => ({ ...p, preferred_time: e.target.value }))}
              className="mt-1"
              data-testid="bono-input-preferred-time"
              step="900"
              min="09:00"
              max="21:00"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="notes">Notas (opcional)</Label>
          <Textarea
            id="notes"
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            placeholder="Comentarios..."
            className="mt-2"
            rows={3}
          />
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-900">
          📞 <strong>Tu reserva no estará confirmada hasta que hablemos contigo.</strong> Una vez completado
          el pago, te llamaremos para confirmar la fecha y hora de tu primera sesión, o tú puedes
          llamarnos al 678 901 086 indicando tu código de bono.
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-halo-taupe/10 flex items-center justify-between">
        <div>
          <p className="text-halo-text text-sm">Total</p>
          <p className="font-heading text-2xl text-halo-brown">{selected.price}€</p>
        </div>
        <Button
          type="submit"
          className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
          data-testid="bono-form-submit"
        >
          Continuar al pago
        </Button>
      </div>
    </form>
  </motion.div>
);


const PaymentStep = ({ selected, form, submitting, onPay, onBack }) => (
  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="max-w-2xl">
    <div className="flex items-center gap-4 mb-8">
      <button
        onClick={onBack}
        disabled={submitting}
        className="p-2 rounded-lg hover:bg-halo-brown/10 transition-colors disabled:opacity-50"
        data-testid="back-to-form"
      >
        <ArrowLeft size={20} className="text-halo-charcoal" />
      </button>
      <h2 className="font-heading text-2xl text-halo-charcoal">Pago seguro</h2>
    </div>

    <div className="bg-halo-surface rounded-2xl p-8 border border-halo-taupe/10">
      <div className="bg-halo-cream rounded-xl p-5 mb-6 space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-halo-text">Bono:</span>
          <span className="text-halo-charcoal font-medium">{selected.name}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-halo-text">Sesiones:</span>
          <span className="text-halo-charcoal">{selected.num_sessions}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-halo-text">Titular:</span>
          <span className="text-halo-charcoal">{form.customer_name}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-halo-text">Email:</span>
          <span className="text-halo-charcoal">{form.customer_email}</span>
        </div>
        <div className="border-t border-halo-taupe/10 pt-3 mt-3 flex justify-between items-baseline">
          <span className="text-halo-charcoal font-medium">Total a pagar:</span>
          <span className="text-halo-brown font-heading text-2xl">{selected.price}€</span>
        </div>
      </div>

      <Button
        onClick={onPay}
        disabled={submitting}
        className="w-full bg-halo-brown hover:bg-halo-charcoal text-halo-cream py-4 text-base"
        data-testid="bono-pay-btn"
      >
        {submitting ? (
          <span className="flex items-center gap-2">
            <Loader2 className="animate-spin" size={18} /> Redirigiendo a Redsys...
          </span>
        ) : (
          `Pagar ${selected.price}€ con tarjeta`
        )}
      </Button>

      <p className="text-xs text-center text-halo-taupe mt-4">
        Pago procesado de forma segura por Redsys (TPV Virtual de CaixaBank).
        <br />
        Recibirás tu bono por email tras el pago.
      </p>
    </div>
  </motion.div>
);

export default BonosPage;
